import { z } from 'zod'
import { lenientNullable, timestampToDateNullable } from '@upadelmatch/core'
import { PLAYER_LEVEL_SOURCES } from '@upadelmatch/core/player-level'

// Player account document — `users/{uid}` (doc id = Firebase Auth UID).
// Shape mirrors docs/data-model.md "Collection: users" (the frozen legacy
// contract): nested maps profile/padel/phone/status/device/location/
// notificationPreferences. Cross-app alignment (decision 2026-07-04):
// `padel.skillLevel` is the canonical nivel 0–7 (same constraint as
// clubmanager CRM `level`), `padel.levelSource` the shared provenance enum,
// and `padel.position` stores clubmanager's side values (`drive`/`reverse`/
// `both`) so the future CRM↔player join is a mapping, not a migration —
// Spanish labels (Derecha/Revés/Indiferente) live only in the UI.

export const PLAYER_POSITIONS = ['drive', 'reverse', 'both'] as const
export type PlayerPosition = (typeof PLAYER_POSITIONS)[number]

/** UI labels for stored positions. "Indiferente" = plays either side. */
export const PLAYER_POSITION_LABELS: Record<PlayerPosition, string> = {
  drive: 'Derecha',
  reverse: 'Revés',
  both: 'Indiferente',
}

export const REGISTRATION_METHODS = ['email_password', 'google_signin', 'phone'] as const

export const UserSchema = z.object({
  uid: z.string(),
  profile: z
    .object({
      name: z.string().nullable().default(null),
      surnames: z.string().nullable().default(null),
      email: z.string().nullable().default(null),
      photoURL: z.string().nullable().default(null),
    })
    .default({}),
  padel: z
    .object({
      // Canonical nivel 0–7, 0.1 steps; self-declared at onboarding, AI/result
      // calibrated later. Same Zod constraint as clubmanager CRM `level`.
      skillLevel: z.number().min(0).max(7).multipleOf(0.1).nullable().default(null),
      levelSource: lenientNullable(z.enum(PLAYER_LEVEL_SOURCES), 'user.padel.levelSource'),
      // Confidence 0–100 (spec/usersmatch/05): written ONLY by the
      // calibration CF (rules block client writes); null = never played.
      levelReliability: z.number().min(0).max(100).nullable().default(null),
      gender: z.string().nullable().default(null),
      position: lenientNullable(z.enum(PLAYER_POSITIONS), 'user.padel.position'),
      preferredHand: z.string().nullable().default(null),
    })
    .default({}),
  phone: z
    .object({
      phoneNumber: z.string().nullable().default(null),
      phonePrefix: z.string().nullable().default(null),
      isPhoneValidated: z.boolean().default(false),
      phoneValidatedAt: timestampToDateNullable,
    })
    .default({}),
  status: z
    .object({
      isDisabled: z.boolean().default(false),
      disabledAt: timestampToDateNullable,
      registrationMethod: lenientNullable(z.enum(REGISTRATION_METHODS), 'user.status.registrationMethod'),
    })
    .default({}),
  /** The club that anchors this player's feed (rebuild addition, research
   *  F8: liquidity concentrates club-first). Usually the club that
   *  distributed the app; editable in Editar perfil. */
  homeClubId: z.string().nullable().default(null),
  location: z
    .object({
      address: z.string().nullable().default(null),
      city: z.string().nullable().default(null),
      administrativeArea: z.string().nullable().default(null),
      country: z.string().nullable().default(null),
      countryCode: z.string().nullable().default(null),
      postalCode: z.string().nullable().default(null),
      latitude: z.number().nullable().default(null),
      longitude: z.number().nullable().default(null),
      placeId: z.string().nullable().default(null),
      formattedAddress: z.string().nullable().default(null),
      /** Geohash precision 9 (@upadelmatch/core/geohash) for proximity queries. */
      geohash: z.string().nullable().default(null),
      /** Partition key: "{countryCode}_{city.toLowerCase()}". */
      cityKey: z.string().nullable().default(null),
    })
    .default({}),
  /** Device web-push tokens (release R1) — owner-writable; the notification
   *  CFs read them via the admin SDK and prune dead ones. */
  fcmTokens: z.array(z.string()).default([]),
  notificationPreferences: z
    .object({
      emailNotifications: z.boolean().default(true),
      browserPushNotifications: z.boolean().default(true),
      smsNotifications: z.boolean().default(false),
      matchInvitations: z.boolean().default(true),
      matchUpdates: z.boolean().default(true),
      systemUpdates: z.boolean().default(true),
      marketingEmails: z.boolean().default(false),
    })
    .default({}),
  device: z
    .object({
      fcmToken: z.string().nullable().default(null),
      lastTokenUpdate: timestampToDateNullable,
    })
    .default({}),
  createdAt: timestampToDateNullable,
  updatedAt: timestampToDateNullable,
})

export type UserDoc = z.output<typeof UserSchema>
